import { usePost } from "@albertine/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useEffectEvent, useRef, useState } from "react";
import type { ChatThreadQueryKey } from "../-data/useChatThread";
import type { ChatThreadInfiniteData } from "../-utils/threadCache";

export function useMarkThreadRead({
    conversationId,
    queryKey,
    requestId,
}: {
    conversationId: string | null;
    queryKey: ChatThreadQueryKey;
    requestId: string | undefined;
}) {
    const queryClient = useQueryClient();
    const lastSuccessfullyMarkedMessageIdRef = useRef<string | null>(null);
    const inFlightMarkedMessageIdRef = useRef<string | null>(null);
    const [retryToken, setRetryToken] = useState(0);

    const { mutate: markConversationMessagesRead } = usePost(
        "markConversationMessagesRead",
    );

    const clearUnreadIdsInCache = useEffectEvent(() => {
        queryClient.setQueryData<ChatThreadInfiniteData>(queryKey, (data) =>
            data
                ? {
                      ...data,
                      pages: data.pages.map((page) => ({
                          ...page,
                          unreadMessageIds: [],
                      })),
                  }
                : data,
        );
    });

    const markThreadRead = useEffectEvent((messageId: string | null) => {
        if (!conversationId || !messageId) {
            return;
        }

        if (lastSuccessfullyMarkedMessageIdRef.current === messageId) {
            return;
        }

        if (inFlightMarkedMessageIdRef.current === messageId) {
            return;
        }

        inFlightMarkedMessageIdRef.current = messageId;
        markConversationMessagesRead(
            {
                param: { conversationId },
                json: { requestId: requestId ?? null },
            },
            {
                onSuccess: () => {
                    lastSuccessfullyMarkedMessageIdRef.current = messageId;
                    if (inFlightMarkedMessageIdRef.current === messageId) {
                        inFlightMarkedMessageIdRef.current = null;
                    }
                    void queryClient.cancelQueries({ queryKey });
                    clearUnreadIdsInCache();
                },
                onError: () => {
                    if (inFlightMarkedMessageIdRef.current === messageId) {
                        inFlightMarkedMessageIdRef.current = null;
                    }
                    setRetryToken((token) => token + 1);
                },
            },
        );
    });

    return { markThreadRead, markRetryToken: retryToken };
}
